import { memo } from "react";
import Page from "components/page/Page";
import { Helmet } from "react-helmet";
import Card from "components/card/Card";
import { Link } from "react-router-dom";
import { styled } from "styled-components";
import { PageCollapseWidth } from "../../constants";

const ToolSection = styled.div`
  margin-bottom: 2rem;
`;

const ToolTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.5rem;

  a {
    color: var(--primary-color);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ToolDescription = styled.p`
  margin-bottom: 0.5rem;
  color: var(--bs-body-color);
`;

const ToolCategory = styled.div`
  margin-bottom: 3rem;

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: var(--primary-color);
    text-transform: uppercase;
  }
`;

const HomePage = () => {
  return (
    <>
      <Helmet>
        <title>ToolWeaver – Free Online Developer & Text Utilities</title>
        <meta
          name="description"
          content="Free online tools for developers and everyday use. Encode and decode Base64, generate GUIDs, create MD5 hashes, and convert URLs instantly with ToolWeaver."
        />
        <link rel="canonical" href="https://toolweaver.com/" />
      </Helmet>
      <Page>
        <Card
          collapseOnWidth={PageCollapseWidth}
          title="Free Online Developer & Text Tools"
        >
          <span className="d-block mb-4">
            Free online tools for developers and internet users. Instantly
            encode, decode, hash, and generate data with no signup required.
          </span>

          <ToolCategory>
            <h3>Base64 Tools</h3>
            <ToolSection>
              <ToolTitle>
                <Link to="/base64encode">Base64 Encoder</Link>
              </ToolTitle>
              <ToolDescription>
                Free online Base64 encoder. Instantly encode text to Base64
                format for safe data transmission and storage.
              </ToolDescription>
            </ToolSection>
            <ToolSection>
              <ToolTitle>
                <Link to="/base64decode">Base64 Decoder</Link>
              </ToolTitle>
              <ToolDescription>
                Free online Base64 decoder. Instantly decode Base64-encoded text
                back to its original format.
              </ToolDescription>
            </ToolSection>
          </ToolCategory>

          <ToolCategory>
            <h3>URL Tools</h3>
            <ToolSection>
              <ToolTitle>
                <Link to="/urlencode">URL Encoder</Link>
              </ToolTitle>
              <ToolDescription>
                Free online URL encoder. Instantly encode text for safe use in
                URLs, converting special characters to percent-encoded format.
              </ToolDescription>
            </ToolSection>
            <ToolSection>
              <ToolTitle>
                <Link to="/urldecode">URL Decoder</Link>
              </ToolTitle>
              <ToolDescription>
                Free online URL decoder. Instantly decode URL-encoded text back
                to its readable format.
              </ToolDescription>
            </ToolSection>
          </ToolCategory>

          <ToolCategory>
            <h3>Generator & Hash Tools</h3>
            <ToolSection>
              <ToolTitle>
                <Link to="/guidgenerate">GUID Generator</Link>
              </ToolTitle>
              <ToolDescription>
                Free online GUID generator. Instantly create unique globally
                unique identifiers (GUIDs) for your projects and applications.
              </ToolDescription>
            </ToolSection>
            <ToolSection>
              <ToolTitle>
                <Link to="/md5hash">MD5 Hash Generator</Link>
              </ToolTitle>
              <ToolDescription>
                Free online MD5 hash generator. Instantly create MD5 hashes for
                text verification, checksums, and data integrity validation.
              </ToolDescription>
            </ToolSection>
          </ToolCategory>

          <span className="d-block">
            We are constantly adding new tools to help with your daily tasks. If
            you have any suggestions for tools you would like to see added, feel
            free to <Link to="/contact">contact us</Link>.
          </span>
        </Card>
      </Page>
    </>
  );
};

export default memo(HomePage);
